/**
 * /terceirizacao/oses — Lista de OSEs (Fase 10).
 *
 * Layout em 3 zonas:
 *   Zona 1: EntityHeader + action "Nova OSE"
 *   Zona 2: KpiStrip (em transito, atrasadas, retornadas aguarda CQ, finalizadas no mes)
 *   Zona 3: Filtros + Tabela
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiAlertTriangle, FiPlus, FiRefreshCw } from "react-icons/fi";

import EntityHeader from "../../../components/EntityHeader";
import KpiStrip from "../../../components/KpiStrip";
import { useAuth } from "../../../context/AuthContext";
import {
  CATEGORIAS,
  listarOses,
  SITUACAO_LABELS,
  SITUACAO_TONES,
  SITUACOES_FILTRO,
} from "../../../services/terceirizacao";
import { PageContainer } from "../../../styles/shared";

import { fmtData, fmtMoeda } from "../../Orcamentos/helpers";
import {
  Card,
  EmptyMsg,
  ErrorMsg,
  Pill,
  SectionTitle,
  Select,
  Table,
  TableWrapper,
} from "../../Orcamentos/styles";


export default function OsesLista() {
  const { request, hasAllPermissions, isMaster } = useAuth();
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filtroSituacao, setFiltroSituacao] = useState("");
  const [filtroCategoria, setFiltroCategoria] = useState("");
  const [apenasAtrasadas, setApenasAtrasadas] = useState(false);

  const podeCriar = isMaster || hasAllPermissions(["terceirizacao:create_ose"]);

  const carregar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = await listarOses(request, {
        situacao: filtroSituacao || undefined,
        categoria: filtroCategoria || undefined,
        atrasadas: apenasAtrasadas || undefined,
      });
      const list = Array.isArray(payload) ? payload : payload?.results || [];
      setItems(list);
    } catch (err) {
      setError(err?.message || "Erro ao carregar OSEs.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [request, filtroSituacao, filtroCategoria, apenasAtrasadas]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const kpis = useMemo(() => {
    const emTransito = items.filter(
      (o) => o.situacao === "ENVIADA" || o.situacao === "EM_PRODUCAO_TERCEIRO",
    );
    const atrasadas = items.filter((o) => o.atrasada);
    const aguardaCq = items.filter((o) => o.situacao === "RETORNADA");
    return [
      {
        label: "Em trânsito",
        value: emTransito.length,
        tone: emTransito.length > 0 ? "info" : "neutral",
      },
      {
        label: "Atrasadas",
        value: atrasadas.length,
        tone: atrasadas.length > 0 ? "danger" : "success",
        sub: atrasadas.length > 0 ? "clique para filtrar" : "",
        onClick: atrasadas.length > 0
          ? () => setApenasAtrasadas(!apenasAtrasadas)
          : undefined,
      },
      {
        label: "Aguarda CQ",
        value: aguardaCq.length,
        tone: aguardaCq.length > 0 ? "warning" : "neutral",
      },
      {
        label: "Total na lista",
        value: items.length,
        tone: "neutral",
      },
    ];
  }, [items, apenasAtrasadas]);

  return (
    <PageContainer>
      <EntityHeader
        title="Ordens de Serviço Externas (OSE)"
        subtitle="Peças em poder de terceiros - laquação, vidraria, metais (ADR-0025)"
        actions={[
          {
            label: "Atualizar",
            icon: <FiRefreshCw size={14} aria-hidden="true" />,
            onClick: carregar,
          },
          ...(podeCriar
            ? [{
                label: "Nova OSE",
                icon: <FiPlus size={14} aria-hidden="true" />,
                onClick: () => navigate("/terceirizacao/oses/criar"),
                primary: true,
              }]
            : []),
        ]}
      />

      <KpiStrip items={kpis} />

      <Card>
        <SectionTitle>Filtros</SectionTitle>
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <label style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 180 }}>
            <span style={{ fontSize: 12, fontWeight: 600 }}>Situação</span>
            <Select
              value={filtroSituacao}
              onChange={(e) => setFiltroSituacao(e.target.value)}
              disabled={loading}
            >
              <option value="">Todas</option>
              {SITUACOES_FILTRO.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </Select>
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 180 }}>
            <span style={{ fontSize: 12, fontWeight: 600 }}>Categoria</span>
            <Select
              value={filtroCategoria}
              onChange={(e) => setFiltroCategoria(e.target.value)}
              disabled={loading}
            >
              <option value="">Todas</option>
              {CATEGORIAS.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </Select>
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 22 }}>
            <input
              type="checkbox"
              checked={apenasAtrasadas}
              onChange={(e) => setApenasAtrasadas(e.target.checked)}
              disabled={loading}
            />
            <span style={{ fontSize: 14 }}>Apenas atrasadas</span>
          </label>
        </div>
      </Card>

      {error && (
        <ErrorMsg>
          <FiAlertTriangle size={14} aria-hidden="true" /> {error}
        </ErrorMsg>
      )}

      <Card>
        <SectionTitle>
          {loading ? "Carregando..." : `${items.length} OSE(s)`}
        </SectionTitle>
        {!loading && items.length === 0 && (
          <EmptyMsg>
            Nenhuma OSE encontrada com os filtros atuais.
          </EmptyMsg>
        )}
        {items.length > 0 && (
          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <th>Número</th>
                  <th>Parceiro</th>
                  <th>Categoria</th>
                  <th>Serviço</th>
                  <th>Peças</th>
                  <th>Valor</th>
                  <th>Situação</th>
                  <th>Prazo</th>
                  <th>Criada em</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr
                    key={it.id}
                    onClick={() => navigate(`/terceirizacao/oses/${it.id}`)}
                    style={{ cursor: "pointer" }}
                  >
                    <td><strong>{it.numero}</strong></td>
                    <td>{it.parceiro_nome}</td>
                    <td>{it.categoria_display || it.categoria}</td>
                    <td style={{ maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                        title={it.descricao_servico}>
                      {it.descricao_servico}
                    </td>
                    <td style={{ textAlign: "center" }}>{it.qtd_ops}</td>
                    {/* Sem valor NAO e zero: e "ninguem informou" — e sem ele
                        a aprovacao no CQ nao gera conta a pagar. */}
                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      {it.valor === null || it.valor === undefined ? (
                        <Pill $tone="warning">sem valor</Pill>
                      ) : (
                        fmtMoeda(it.valor)
                      )}
                    </td>
                    <td>
                      <Pill $tone={SITUACAO_TONES[it.situacao] || "neutral"}>
                        {SITUACAO_LABELS[it.situacao] || it.situacao}
                      </Pill>
                      {it.atrasada && (
                        <Pill $tone="danger" style={{ marginLeft: 6 }}>
                          {it.dias_em_atraso}d atraso
                        </Pill>
                      )}
                    </td>
                    <td>{it.prazo_retorno_esperado ? fmtData(it.prazo_retorno_esperado) : "—"}</td>
                    <td>{fmtData(it.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrapper>
        )}
      </Card>
    </PageContainer>
  );
}
